import { NonnaRepository } from '../../domain/services/nonna-repository'

const NONNA_SELECT = `
  id,
  full_name,
  headline,
  bio,
  hourly_rate,
  rating,
  review_count,
  years_experience,
  nonna_badges(badge)
`

function rowToNonna(row) {
  return {
    id: row.id,
    name: row.full_name,
    headline: row.headline,
    bio: row.bio,
    hourlyRate: row.hourly_rate,
    rating: Number(row.rating),
    reviewCount: row.review_count,
    yearsExperience: row.years_experience,
    trustBadges: (row.nonna_badges ?? []).map((entry) => entry.badge),
  }
}

export class SupabaseNonnaRepository extends NonnaRepository {
  constructor(client) {
    super()
    this.client = client
  }

  async fetchFeaturedNonnas() {
    const { data, error } = await this.client
      .from('nonnas')
      .select(NONNA_SELECT)
      .eq('is_active', true)
      .order('rating', { ascending: false })
      .limit(10)

    if (error) throw error
    return (data ?? []).map(rowToNonna)
  }

  async fetchNonnaById(id) {
    const { data, error } = await this.client
      .from('nonnas')
      .select(NONNA_SELECT)
      .eq('id', id)
      .single()

    if (error) throw error
    return rowToNonna(data)
  }

  async fetchBookings(parentId) {
    const { data, error } = await this.client
      .from('bookings')
      .select(`
        id, parent_id, nonna_id, status, total_amount,
        scheduled_start, scheduled_end, location_label, created_at,
        nonnas(full_name)
      `)
      .eq('parent_id', parentId)
      .order('scheduled_start', { ascending: false })

    if (error) throw error
    return (data ?? []).map((row) => ({
      id: row.id,
      parentId: row.parent_id,
      nonnaId: row.nonna_id,
      nonnaName: row.nonnas?.full_name ?? '',
      status: row.status,
      totalAmount: row.total_amount,
      scheduledStart: row.scheduled_start,
      scheduledEnd: row.scheduled_end,
      locationLabel: row.location_label,
      createdAt: row.created_at,
    }))
  }

  async createBooking(draft, parentId) {
    const SERVICE_FEE = 3
    const hours = draft.schedule?.durationHours ?? 1
    const hourlyRate = draft.selectedNonna?.hourlyRate ?? 0
    const total = Number(hourlyRate) * hours + SERVICE_FEE

    // Upsert children
    if (draft.children && draft.children.length > 0) {
      const childRows = draft.children.map((c) => ({
        id: c.id,
        parent_id: parentId,
        full_name: c.fullName,
        age_years: c.ageYears,
        notes: c.notes ?? null,
      }))
      const { error: childError } = await this.client
        .from('children')
        .upsert(childRows, { onConflict: 'id' })
      if (childError) throw childError
    }

    const { data, error } = await this.client
      .from('bookings')
      .insert({
        parent_id: parentId,
        nonna_id: draft.selectedNonna?.id ?? null,
        status: 'confirmed',
        total_amount: total,
        scheduled_start: draft.schedule?.scheduledStart ?? null,
        scheduled_end: draft.schedule?.scheduledEnd ?? null,
        location_label: draft.schedule?.locationLabel ?? null,
      })
      .select()
      .single()

    if (error) throw error
    return { id: data.id, ...data }
  }

  async fetchPaymentMethods(parentId) {
    const { data, error } = await this.client
      .from('payment_methods')
      .select('id, parent_id, brand, last_four, is_default, created_at')
      .eq('parent_id', parentId)
      .order('is_default', { ascending: false })

    if (error) throw error
    return (data ?? []).map((row) => ({
      id: row.id,
      parentId: row.parent_id,
      brand: row.brand,
      lastFour: row.last_four,
      isDefault: row.is_default,
      createdAt: row.created_at,
    }))
  }

  async fetchPaymentTransactions(parentId) {
    const { data, error } = await this.client
      .from('payment_transactions')
      .select('id, booking_id, amount, status, created_at, bookings!inner(parent_id)')
      .eq('bookings.parent_id', parentId)
      .order('created_at', { ascending: false })

    if (error) throw error
    return (data ?? []).map((row) => ({
      id: row.id,
      bookingId: row.booking_id,
      amount: row.amount,
      status: row.status,
      createdAt: row.created_at,
    }))
  }

  async fetchFAQEntries() {
    const { data, error } = await this.client
      .from('faq_entries')
      .select('id, question, answer, display_order')
      .order('display_order', { ascending: true })

    if (error) throw error
    return (data ?? []).map((row) => ({
      id: row.id,
      question: row.question,
      answer: row.answer,
      displayOrder: row.display_order,
    }))
  }
}